import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cable, Save, PlugZap, FlaskConical, CheckCircle2, AlertCircle, Clock, Loader2 } from "lucide-react";

const VENDORS = {
  sysdyne: {
    title: "Sysdyne · ConcreteGO / Pioneer", blurb: "Two-way: pull completed tickets & actual weights via the Webcrete API (or Pioneer ULINK export on the plant agent); optionally push dispatch orders as tickets.",
    fields: [["endpoint", "Webcrete endpoint"], ["app_id", "App ID"], ["app_key", "App Key", "password"], ["username", "API user"], ["password", "API password", "password"], ["plant_code", "Sysdyne plant code"]],
  },
  dorner: {
    title: "DornerBatch", blurb: "Read-only: the plant agent polls a Dorner SELECT-only reporting view (or watches the batch-report export folder) and streams each completed load here.",
    fields: [["sql_dsn", "SQL DSN (read-only login)", "password"], ["sql_view", "Reporting view"], ["export_dir", "Report export folder (fallback)"], ["plant_code", "Dorner plant code"]],
  },
};

function Field({ id, label, type, value, onChange }) {
  return (
    <div>
      <Label className="label-tag">{label}</Label>
      <Input type={type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-none bg-transparent border-industrial mono text-xs" data-testid={`connector-${id}`} placeholder={type === "password" ? "•••• (leave blank to keep)" : ""} />
    </div>
  );
}

function VendorPanel({ vendor, cfg, onSaved }) {
  const meta = VENDORS[vendor];
  const [form, setForm] = useState(cfg);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  useEffect(() => setForm(cfg), [cfg]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(null); } };
  const save = () => run("save", async () => { const { data } = await api.put(`/connectors/${vendor}`, { config: form }); onSaved(vendor, data); toast.success(`${meta.title} saved`); });
  const test = () => run("test", async () => { const { data } = await api.post(`/connectors/${vendor}/test`); setResult(data); });
  const simulate = () => run("sim", async () => { const { data } = await api.post(`/connectors/${vendor}/simulate-load`); toast.success(`Simulated ${vendor} load ingested · ${data.within_tolerance ? "within tolerance" : `OOT: ${data.out_of_tolerance.join(", ")}`}`); onSaved(); });
  return (
    <div className="border border-industrial p-4 space-y-3" data-testid={`connector-panel-${vendor}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="heading font-black uppercase text-sm">{meta.title}</div>
          <div className="text-[11px] text-muted-industrial">{meta.blurb}</div>
        </div>
        <span className={`mono text-[9px] uppercase px-1.5 py-0.5 border ${form.enabled ? "border-emerald-500 text-emerald-400" : "border-industrial text-muted-industrial"}`} data-testid={`connector-status-${vendor}`}>{form.enabled ? "Enabled" : "Off"}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="label-tag">Connector</Label>
          <Select value={form.enabled ? "on" : "off"} onValueChange={(v) => set("enabled")(v === "on")}>
            <SelectTrigger className="rounded-none mono text-xs" data-testid={`connector-enabled-${vendor}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="off">Off</SelectItem><SelectItem value="on">Enabled</SelectItem></SelectContent>
          </Select>
        </div>
        {vendor === "sysdyne" ? (
          <div>
            <Label className="label-tag">Mode</Label>
            <Select value={form.mode} onValueChange={set("mode")}>
              <SelectTrigger className="rounded-none mono text-xs" data-testid="connector-mode-sysdyne"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="read_only">Read-only · batch results in</SelectItem><SelectItem value="bidirectional">Send orders · two-way</SelectItem></SelectContent>
            </Select>
          </div>
        ) : (
          <div>
            <Label className="label-tag">Method</Label>
            <Select value={form.method} onValueChange={set("method")}>
              <SelectTrigger className="rounded-none mono text-xs" data-testid="connector-method-dorner"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="sql">SQL reporting view (preferred)</SelectItem><SelectItem value="report">Batch-report export folder</SelectItem></SelectContent>
            </Select>
          </div>
        )}
        {meta.fields.map(([k, l, t]) => <Field key={k} id={`${vendor}-${k}`} label={l} type={t} value={form[k]} onChange={set(k)} />)}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="rounded-none" onClick={save} disabled={busy === "save"} data-testid={`connector-save-${vendor}`}><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
        <Button size="sm" variant="outline" className="rounded-none" onClick={test} disabled={busy === "test"} data-testid={`connector-test-${vendor}`}>{busy === "test" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PlugZap className="w-3.5 h-3.5 mr-1" />} Test connection</Button>
        <Button size="sm" variant="outline" className="rounded-none" onClick={simulate} disabled={busy === "sim"} data-testid={`connector-simulate-${vendor}`}><FlaskConical className="w-3.5 h-3.5 mr-1" /> Simulate completed load</Button>
      </div>
      {result && (
        <div className="space-y-1" data-testid={`connector-test-result-${vendor}`}>
          {result.checks.map((c) => (
            <div key={c.name} className="flex items-start gap-2 mono text-[11px]">
              {c.pending ? <Clock className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" /> : c.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" /> : <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />}
              <span><span className="font-bold">{c.name}</span> · <span className="text-muted-industrial">{c.detail}</span></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlantConnectorsCard() {
  const [cfg, setCfg] = useState(null);
  const [loads, setLoads] = useState([]);
  const [orders, setOrders] = useState([]);
  const load = useCallback(async () => {
    const [c, l, o] = await Promise.all([api.get("/connectors"), api.get("/connectors/loads?limit=8"), api.get("/connectors/sysdyne/orders")]);
    setCfg(c.data); setLoads(l.data); setOrders(o.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const onSaved = (vendor, data) => { if (vendor) setCfg((c) => ({ ...c, [vendor]: data })); else load(); };
  if (!cfg) return null;
  return (
    <div className="card-industrial p-5" data-testid="plant-connectors-card">
      <div className="flex items-center gap-2 mb-1"><Cable className="w-4 h-4 text-primary-orange" /><div className="label-tag">Plant Connectors · Sysdyne & DornerBatch</div></div>
      <p className="text-xs text-muted-industrial mono mb-4">Every completed load from the vendor batch computer lands in the Batcher Console, Load Recall and docket record — idempotent on batch number. Runs read-only on the plant agent (<span className="text-ink">plant-agent/connector.py</span>); the cloud never touches the PLC.</p>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {Object.keys(VENDORS).map((v) => <VendorPanel key={v} vendor={v} cfg={cfg[v]} onSaved={onSaved} />)}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="label-tag mb-1">Recent connector loads</div>
          <div className="border border-industrial divide-y divide-industrial" data-testid="connector-loads-list">
            {loads.length === 0 && <div className="px-2 py-2 mono text-[11px] text-muted-industrial">No loads received yet</div>}
            {loads.map((l) => (
              <div key={l.id} className="flex items-center gap-3 px-2 py-1.5 mono text-[11px]" data-testid={`connector-load-${l.batch_number}`}>
                <span className="uppercase text-primary-orange w-16">{l.vendor}</span><span className="font-bold">{l.batch_number}</span><span>{l.truck_number}</span><span>{l.mix_code}</span><span>{l.quantity_m3} m³</span>
                <span className={`ml-auto ${l.within_tolerance ? "text-emerald-400" : "text-red-400"}`}>{l.within_tolerance ? "IN SPEC" : "OOT"}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="label-tag mb-1">Sysdyne order outbox</div>
          <div className="border border-industrial divide-y divide-industrial" data-testid="connector-outbox-list">
            {orders.length === 0 && <div className="px-2 py-2 mono text-[11px] text-muted-industrial">No orders queued — use "Send to Sysdyne" on a job (two-way mode)</div>}
            {orders.map((o) => (
              <div key={o.id} className="flex items-center gap-3 px-2 py-1.5 mono text-[11px]" data-testid={`connector-order-${o.external_order_id}`}>
                <span className="font-bold">{o.payload?.customer}</span><span>{o.payload?.mix_code}</span><span>{o.payload?.quantity_m3} m³</span>
                <span className="ml-auto uppercase border border-amber-500 text-amber-400 px-1.5">{o.state}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
